import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { RaffleCard } from '@/components/RaffleCard';
import { RaffleStatus, RaffleCategory } from '@/types';
import type { Raffle } from '@/types';
import { Dices, Filter, Search, Trophy, Users, Coins, CheckCircle } from 'lucide-react';
import { api } from '@/services/api';

export function RafflesPage() {
  const { raffles, fetchRaffles } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<RaffleCategory | 'all'>('all');
  const [tab, setTab] = useState<'active' | 'completed'>('active');
  const [completedRaffles, setCompletedRaffles] = useState<Raffle[]>([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);

  useEffect(() => {
    fetchRaffles();
  }, [fetchRaffles]);

  useEffect(() => {
    if (tab === 'completed' && completedRaffles.length === 0) {
      loadCompleted();
    }
  }, [tab]);

  const loadCompleted = async () => {
    try {
      setLoadingCompleted(true);
      const data = await api.getCompletedRaffles();
      setCompletedRaffles(data);
    } catch {
      // silent
    } finally {
      setLoadingCompleted(false);
    }
  };

  // Filter raffles
  const filteredRaffles = raffles.filter((raffle) => {
    const matchesSearch = raffle.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         raffle.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || raffle.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate stats
  const totalPrizes = raffles.reduce((sum, r) => sum + (r.prizes?.length || 0), 0);
  const totalParticipants = raffles.reduce((sum, r) => sum + r.participants_count, 0);
  const activeRaffles = raffles.filter(r => r.status === RaffleStatus.ACTIVE).length;

  const getCategoryLabel = (category: RaffleCategory | 'all') => {
    switch (category) {
      case 'all':
        return '🎯 Все';
      case RaffleCategory.ELECTRONICS:
        return '📱 Электроника';
      case RaffleCategory.TRANSPORT:
        return '🚲 Транспорт';
      case RaffleCategory.GAMING:
        return '🎮 Игровое';
      case RaffleCategory.MONEY:
        return '💰 Деньги и коды';
      case RaffleCategory.ACCESSORIES:
        return '👜 Аксессуары';
      case RaffleCategory.OTHER:
        return '🎁 Другое';
      default:
        return '🎯 Все';
    }
  };

  return (
    <div className="min-h-screen bg-tg-theme-bg pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Dices className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">Розыгрыши</h1>
            <p className="text-purple-100 text-sm">
              {activeRaffles} активных розыгрышей
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Trophy className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
            <p className="text-xs text-purple-100 mb-1">Призов</p>
            <p className="text-sm font-bold">{totalPrizes}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-blue-300" />
            <p className="text-xs text-purple-100 mb-1">Билетов</p>
            <p className="text-sm font-bold">{totalParticipants}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Coins className="w-5 h-5 mx-auto mb-1 text-green-300" />
            <p className="text-xs text-purple-100 mb-1">Розыгрышей</p>
            <p className="text-sm font-bold">{raffles.length}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <button
          onClick={() => setTab('active')}
          className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
            tab === 'active'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <Dices size={16} />
          Активные
        </button>
        <button
          onClick={() => setTab('completed')}
          className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
            tab === 'completed'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <CheckCircle size={16} />
          Завершённые
        </button>
      </div>

      {/* Search & Filters — active tab only */}
      {tab === 'active' && <div className="p-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Поиск розыгрышей..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-tg-theme-text focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
          {(['all', RaffleCategory.ELECTRONICS, RaffleCategory.TRANSPORT, RaffleCategory.GAMING, RaffleCategory.MONEY, RaffleCategory.ACCESSORIES, RaffleCategory.OTHER] as const).map((category) => (
            <button
              key={category}
              onClick={() => setFilterCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filterCategory === category
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {getCategoryLabel(category)}
            </button>
          ))}
        </div>
      </div>}

      {/* Raffles List */}
      {tab === 'active' ? (
        <div className="px-4 space-y-4">
          {filteredRaffles.length === 0 ? (
            <div className="text-center py-12">
              <Dices className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-2">
                {searchQuery || filterCategory !== 'all'
                  ? 'Розыгрыши не найдены'
                  : 'Нет доступных розыгрышей'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-600">
                {searchQuery || filterCategory !== 'all'
                  ? 'Попробуйте изменить фильтры'
                  : 'Новые розыгрыши появятся здесь'}
              </p>
            </div>
          ) : (
            filteredRaffles.map((raffle) => (
              <RaffleCard key={raffle.id} raffle={raffle} />
            ))
          )}
        </div>
      ) : (
        <div className="px-4 space-y-4 py-4">
          {loadingCompleted ? (
            <div className="text-center py-12 text-gray-500 text-sm">Загрузка...</div>
          ) : completedRaffles.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Завершённых розыгрышей пока нет</p>
            </div>
          ) : (
            completedRaffles.map((raffle) => (
              <RaffleCard key={raffle.id} raffle={raffle} />
            ))
          )}
        </div>
      )}

      {/* Info Banner */}
      {raffles.length > 0 && (
        <div className="p-4 mt-6">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
            <div className="flex items-start space-x-3">
              <Trophy className="w-6 h-6 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                  Как это работает?
                </h3>
                <ul className="text-sm text-purple-700 dark:text-purple-300 space-y-1">
                  <li>• Выберите розыгрыш и купите билеты</li>
                  <li>• Чем больше билетов, тем выше шанс победы</li>
                  <li>• Победители получают ценные призы</li>
                  <li>• Результаты честные и прозрачные</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
